<script>
import baseMixin from '@mixins/baseMixin.js'
import _ from 'lodash'
import formatDateRelative from '@utils/format-date-relative.js'
import formatDate from '@utils/format-date.js'
import UserAvatar from '@components/user/user-avatar.vue'
import CandidateFilterMenu from '@components/message/candidate-filter-menu.vue'
import ReactiveRelativeTime from '@components/general/reactive-relative-time.vue'
import { mapActions, mapGetters } from 'vuex'
import CandidateListSkeleton from '../loaders/candidate-list-skeleton.vue'

export default {
  name: 'CandidateList',
  components: {
    UserAvatar,
    CandidateFilterMenu,
    ReactiveRelativeTime,
    CandidateListSkeleton,
  },
  mixins: [baseMixin],

  data() {
    return {
      selected: null,
      search: '',
      searching: false,
      showMessageList: true,
      typeFilter: 'All',
      renderStart: 0,
      limit: 10,
      title: 'My Messages',
    }
  },
  computed: {
    ...mapGetters({
      items: 'message/getSummaryMessageList',
      current: 'message/currentlyViewing',
      isLoadingRecentMessages: 'message/isLoadingRecentMessages',
    }),
    compScrollHeight() {
      return {
        // 76px (top nav bar) + 64px (list bar) + 24px debug banner
        height: `calc(100vh - ${76 + 64 + (this.isDev ? 24 : 0)}px)`,
        overflowY: 'auto',
      }
    },

    filteredItems() {
      let items = this.items

      // filter on contact type
      if (this.typeFilter !== 'All')
        items = this.items.filter((item) => {
          return item.contactType
            .toLowerCase()
            .includes(this.typeFilter.toLowerCase())
        })

      return _.orderBy(
        items.filter((item) => {
          return (
            item.name.toLowerCase().includes(this.search.toLowerCase()) ||
            item.contactType.toLowerCase().includes(this.search.toLowerCase())
          )
        }),
        ['unread', 'timestamp'],
        ['desc', 'desc']
      )
    },
    displayMessageFilters() {
      return this.showMessageList
    },
  },
  watch: {
    isMobileViewPort() {
      if (!this.isMobileViewPort) {
        this.showMessageList = true
      }
    },
  },
  created() {
    // If loading messages via query string, preselect candidate in list
    if (this.$route.query.cid)
      this.selected = this.filteredItems.findIndex(
        (x) => x.id === parseInt(this.$route.query.cid)
      )
  },
  methods: {
    ...mapActions({
      setLastMessageToRead: 'message/setLastMessageToRead',
    }),
    loadMyMessages() {
      this.selected = null
      this.$emit('clear-reading-pane')
      this.title = 'My Messages'
      this.$store.dispatch('message/loadMessages')
    },
    loadMyTeamsMessages() {
      this.$emit('clear-reading-pane')
      this.title = "My Team's Messages"
      this.selected = null
      this.$store.dispatch('message/loadMyTeamMessages')
    },
    convertToRelative(timestamp) {
      if (!timestamp) return ''
      return formatDateRelative(timestamp, undefined, true)
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return ''
      return formatDate(timestamp, 'llll')
    },
    handleClick(item) {
      if (item.unread) this.setLastMessageToRead(item.id)

      // In mobile view, automatically close message list and clear search query
      if (this.isMobileViewPort) {
        this.searching = false
        this.search = ''
        this.showMessageList = false
      }

      if (this.current === item.id) {
        this.selected = this.filteredItems.findIndex(
          (x) => x.id === parseInt(this.current)
        )
        return
      }

      this.$emit('load-messages', item.id)
    },
    updateTypeFilter(val) {
      this.typeFilter = val
    },
    handleInfiniteScroll() {
      this.limit += 10
    },
  },
}
</script>

<template>
  <v-navigation-drawer permanent width="600">
    <v-toolbar :dense="isMobileViewPort" flat>
      <v-btn
        v-if="isMobileViewPort"
        small
        icon
        @click="showMessageList = !showMessageList"
      >
        <v-icon>{{
          showMessageList ? 'mdi-chevron-up' : 'mdi-chevron-down'
        }}</v-icon>
      </v-btn>
      <v-toolbar-title :class="isMobileViewPort ? 'subtitle-2' : ''">
        {{ title }}
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-btn
        v-if="displayMessageFilters"
        title="Search"
        icon
        @click="searching = !searching"
      >
        <v-icon>mdi-magnify</v-icon>
      </v-btn>
      <v-expand-x-transition>
        <v-card v-show="searching" flat>
          <v-text-field
            v-model="search"
            label="Search..."
            hide-details
            single-line
            :autofocus="searching"
            class="align-center"
          ></v-text-field>
        </v-card>
      </v-expand-x-transition>

      <v-menu
        v-if="displayMessageFilters"
        :close-on-content-click="false"
        :nudge-width="200"
        offset-x
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn title="Filters" icon v-bind="attrs" v-on="on">
            <v-icon>mdi-filter-variant</v-icon>
          </v-btn>
        </template>

        <CandidateFilterMenu
          :type="typeFilter"
          @select-my-messages="loadMyMessages"
          @select-team-messages="loadMyTeamsMessages"
          @type-updated="updateTypeFilter"
        />
      </v-menu>
    </v-toolbar>
    <v-divider></v-divider>
    <CandidateListSkeleton v-if="isLoadingRecentMessages" />
    <v-expand-transition v-else>
      <div v-if="showMessageList">
        <v-list two-line class="pt-0 scrollableList" :style="compScrollHeight">
          <v-list-item-group v-model="selected" active-class="blue--text">
            <template
              v-for="(item, index) in filteredItems.slice(renderStart, limit)"
            >
              <v-list-item
                :key="`candidate-list-item-index-${item.id}`"
                @click="handleClick(item)"
              >
                <template v-slot="{ active }">
                  <v-list-item-avatar>
                    <UserAvatar
                      :name="item.name"
                      :image-url="item.img || ''"
                      size="md"
                    />
                  </v-list-item-avatar>

                  <v-list-item-content :data-something="active">
                    <v-list-item-title
                      :class="[
                        'subtitle-2',
                        item.unread ? 'font-weight-bold' : '',
                      ]"
                    >
                      <v-badge
                        class="mt-0"
                        color="blue"
                        :value="item.unread"
                        inline
                        dot
                      >
                        {{ item.name }}
                      </v-badge>
                    </v-list-item-title>

                    <v-list-item-subtitle
                      class="text--primary caption"
                      v-text="item.contactType"
                    ></v-list-item-subtitle>

                    <v-list-item-subtitle
                      class="caption"
                      v-text="item.preview"
                    ></v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-list-item-action-text
                      class="candidate-list_timestamp"
                      :title="formatTimestamp(item.timestamp)"
                    >
                      <ReactiveRelativeTime :date="item.timestamp" />
                    </v-list-item-action-text>
                  </v-list-item-action>
                </template>
              </v-list-item>

              <v-divider
                v-if="index < items.length - 1"
                :key="`candidate-list-divider-index--${index}`"
              ></v-divider>
            </template>
          </v-list-item-group>
          <v-card v-intersect="handleInfiniteScroll"></v-card>
        </v-list>
      </div>
    </v-expand-transition>
  </v-navigation-drawer>
</template>
